import { useEffect, useState } from "react";
import "./styles.scss";
import DBInput from "../../../../component/global/DBInput";
import { t } from "i18next";
import Skeleton from "react-loading-skeleton";
import { useRequest } from "../../../../hooks/helperData/useRequest";
import {
    SETTING_ITEMS_TYPE,
    SETTING_ITEMS_TYPE_TREE,
} from "../../../../helperData/ApiAddress";
import DBSearchableTree from "../../../../component/global/DBSearchableTree";
import {
    findTreeNodeById,
    normalizeTreeResponse,
} from "../../../../component/global/DBSearchableTree/treeUtils";
import TreeDecorator from "./TreeDecorator";
import i18n from "./../../../../i18n/i18n";
import ItemTypeModal from "./ItemTypeModal";
import AIItemTypeModal from "./AIItemTypeModal";
import DBConfirmModal from "../../../../component/global/DBConfirmModal";
import DBButton from "../../../../component/global/DBButton";
import { Add, MagicStar } from "iconsax-react";
import { AiCoinHint } from "../../../../features/ai-billing/AiCoinGate";

const syncItemtypeSelection = (treeData, currentSelection, onChange) => {
    const nodes = normalizeTreeResponse(treeData);
    if (!nodes.length) {
        if (currentSelection) onChange(undefined);
        return;
    }
    if (!currentSelection) {
        onChange(nodes[0]);
        return;
    }
    const found = findTreeNodeById(nodes, currentSelection.id);
    onChange(found || nodes[0]);
};

export default function ExpSettingsItemTypes({
    onChange,
    itemTypeRefresh,
    selectedItemtype,
}) {
    const [companyList, setCompanyList] = useState([]);
    const [noLoader, setNoLoader] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [itemTypeModalShow, setItemTypeModalShow] = useState(false);
    const [aiItemTypeModalShow, setAIItemTypeModalShow] = useState(false);
    const [itemTypeEditMode, setItemTypeEditMode] = useState(false);
    const [deleteId, setDeleteId] = useState();
    const [confirmationModalShow, setConfirmationModalShow] = useState(false);

    const [getCompanyList, companyListLoader] = useRequest(
        SETTING_ITEMS_TYPE_TREE,
        "GET"
    );

    const [deleteCompanyList, deleteCompanyListLoader] = useRequest(
        SETTING_ITEMS_TYPE + `${deleteId}/`,
        "DELETE"
    );

    const applyTree = (payload, preferredSelection) => {
        const tree = normalizeTreeResponse(payload);
        setCompanyList(tree);
        syncItemtypeSelection(tree, preferredSelection, onChange);
        return tree;
    };

    const refreshTree = (preferredSelection) => {
        return getCompanyList()
            .then((r) => applyTree(r, preferredSelection))
            .catch(() => {
                setCompanyList([]);
                return [];
            });
    };

    useEffect(() => {
        setNoLoader(false);
        refreshTree(selectedItemtype);
    }, []);

    useEffect(() => {
        if (typeof itemTypeRefresh === "boolean") {
            setNoLoader(true);
            refreshTree(selectedItemtype);
        }
    }, [itemTypeRefresh]);

    const editItemType = (e, id) => {
        e.stopPropagation();
        setItemTypeEditMode(id);
        setItemTypeModalShow(true);
    };

    const deleteItemType = (e, id) => {
        e.stopPropagation();
        setDeleteId(id);
        setConfirmationModalShow(true);
    };

    const deleteItemTypeReq = () => {
        const deletedId = deleteId;
        deleteCompanyList().then(() => {
            setNoLoader(true);
            setConfirmationModalShow(false);
            refreshTree(
                selectedItemtype?.id === deletedId
                    ? undefined
                    : selectedItemtype
            );
        });
    };

    return (
        <div className="exp-settings-companies">
            <ItemTypeModal
                showModal={itemTypeModalShow}
                onHide={() => setItemTypeModalShow(false)}
                editMode={itemTypeEditMode}
                onSuccess={(createdItem) => {
                    setNoLoader(true);
                    getCompanyList()
                        .then((r) => {
                            const tree = normalizeTreeResponse(r);
                            setCompanyList(tree);
                            if (createdItem?.id) {
                                const node = findTreeNodeById(
                                    tree,
                                    createdItem.id
                                );
                                if (node) {
                                    onChange(node);
                                    return;
                                }
                            }
                            syncItemtypeSelection(
                                tree,
                                selectedItemtype,
                                onChange
                            );
                        })
                        .catch(() => setCompanyList([]));
                }}
            />
            <AIItemTypeModal
                showModal={aiItemTypeModalShow}
                onHide={() => setAIItemTypeModalShow(false)}
                onSuccess={(result) => {
                    setNoLoader(true);
                    getCompanyList()
                        .then((r) => {
                            const tree = normalizeTreeResponse(r);
                            setCompanyList(tree);
                            const firstCreatedId = result?.tree?.[0]?.id;
                            const node = findTreeNodeById(
                                tree,
                                firstCreatedId
                            );
                            if (node) {
                                onChange(node);
                                return;
                            }
                            syncItemtypeSelection(
                                tree,
                                selectedItemtype,
                                onChange
                            );
                        })
                        .catch(() => setCompanyList([]));
                }}
            />
            <DBConfirmModal
                show={confirmationModalShow}
                onHide={() => setConfirmationModalShow(false)}
                onConfirm={() => deleteItemTypeReq(deleteId)}
                title={t("itemtype.delete.title")}
                description={t("itemtype.delete.description")}
                btnTitle={t("itemtype.delete.title")}
                loading={deleteCompanyListLoader}
            />
            <div>
                <h1>{t("setting.sidebar.title")}</h1>
                <div className="s-toolbar">
                    <DBInput
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={t("setting.sidebar.search.placeholder")}
                        size="large"
                        allowClear
                    />
                </div>
            </div>
            <div className="s-list">
                {companyListLoader && !noLoader ? (
                    <Skeleton count={5} height={45} />
                ) : (
                    <DBSearchableTree
                        data={companyList}
                        searchTerm={searchTerm}
                        notfoundMessage={t("company.not.found")}
                        decorators={TreeDecorator}
                        selectedNodeHandle={onChange}
                        selectedNode={selectedItemtype}
                        direction={i18n.dir()}
                        functions={{ editItemType, deleteItemType }}
                    />
                )}
            </div>
            <div className="button-section">
                <DBButton
                    className="ai-item-type-button"
                    caption={
                        <>
                            <MagicStar />
                            <span>{t("ai.itemtype.button")}</span>
                            <AiCoinHint operation="item_type_tree" />
                        </>
                    }
                    onClick={() => setAIItemTypeModalShow(true)}
                />
                <DBButton
                    caption={
                        <>
                            <Add />
                            <span>{t("setting.sidebar.add.button")}</span>
                        </>
                    }
                    outlined
                    onClick={() => {
                        setItemTypeEditMode(false);
                        setItemTypeModalShow(true);
                    }}
                />
            </div>
        </div>
    );
}
